import * as React from 'react';
import { KeyboardEvent, MutableRefObject, useCallback, useState } from 'react';
import { Vector } from '../helpers/Vector';
import { NodeLibraryPanel } from './NodeLibrary/NodeLibraryPanel';
import { NodeLibraryPreview } from './NodeLibrary/NodeLibraryPreview';
import type { CallIntegrationRequest, CallIntegrationResponse, FunctionDefinition, IntegrationAccess, Node } from '@glueapp/graph-types';
import { LibraryElement } from '@/GraphEditor/drawingLayer/NodeLibrary/LibraryElement';
import { getViewPortCenterFromGraphContainer } from '@/GraphEditor/drawingLayer/helpers/getViewPortCenterFromGraphContainer';
import { screenToWorldCoordinate } from '@/GraphEditor/drawingLayer/helpers/coordinateSystemMapping';

export function useNodeLibrary(
    addNode: (libraryElement: LibraryElement, position: Vector) => void,
    libraryElements: LibraryElement[],
    nodeDefinitions: Record<string, Node>,
    containerRef: MutableRefObject<HTMLDivElement | null>,
    zoomLevel: number,
    integrationAccesses: IntegrationAccess[],
    callIntegration: (integrationAccess: IntegrationAccess, requestConfig: CallIntegrationRequest) => Promise<CallIntegrationResponse<any>>,
    functions: FunctionDefinition[],
    latestMousePosition: MutableRefObject<Vector>,
) {
    const [nodeLibrary, setNodeLibrary] = useState<{ browsing: boolean }>({ browsing: false });
    const [previewWorldPosition, setPreviewWorldPosition] = useState<Vector | null>(null);
    const [selectedElement, setSelectedElement] = useState<LibraryElement | undefined>(undefined);

    const captureWorldPosition = useCallback(() => {
        const container = containerRef.current;
        if (!container) return null;

        const mousePos = latestMousePosition.current;
        // If mouse never moved, fall back to viewport center
        if (mousePos.x === 0 && mousePos.y === 0) {
            return getViewPortCenterFromGraphContainer(container, zoomLevel);
        }

        return screenToWorldCoordinate(mousePos, { x: container.scrollLeft, y: container.scrollTop }, zoomLevel);
    }, [containerRef, latestMousePosition, zoomLevel]);

    const handleNodeLibraryNodeSelect = useCallback(
        (libraryElement: LibraryElement) => {
            const containerElement = containerRef.current;
            if (containerElement) {
                const basePosition = previewWorldPosition ?? getViewPortCenterFromGraphContainer(containerElement, zoomLevel);
                // The reducer subtracts {200, 100} from the position to center the node on the
                // click point. We add it back so the placed node lands where the preview was shown.
                const position = previewWorldPosition ? { x: basePosition.x + 200, y: basePosition.y + 100 } : basePosition;
                addNode(libraryElement, position);
                setNodeLibrary({ browsing: false });
                setPreviewWorldPosition(null);
                setSelectedElement(undefined);
                // Restore focus to the graph container so the Tab hotkey can reopen the
                // library; otherwise focus is lost and Tab cycles floating UI elements.
                containerElement.focus();
            }
        },
        [addNode, containerRef, zoomLevel, previewWorldPosition],
    );

    const showNodeLibrary = useCallback(
        (useCenter?: boolean) => {
            const container = containerRef.current;
            const worldPos = useCenter && container ? getViewPortCenterFromGraphContainer(container, zoomLevel) : captureWorldPosition();
            setPreviewWorldPosition(worldPos);
            setNodeLibrary({ browsing: true });
        },
        [captureWorldPosition, containerRef, zoomLevel],
    );

    const hideNodeLibrary = useCallback(() => {
        setNodeLibrary({ browsing: false });
        setPreviewWorldPosition(null);
        setSelectedElement(undefined);
    }, []);

    const toggleNodeLibrary = useCallback(() => {
        if (nodeLibrary.browsing) {
            hideNodeLibrary();
        } else {
            showNodeLibrary();
        }
    }, [nodeLibrary.browsing, hideNodeLibrary, showNodeLibrary]);

    const toggleNodeLibraryAtCenter = useCallback(() => {
        if (nodeLibrary.browsing) {
            hideNodeLibrary();
        } else {
            showNodeLibrary(true);
        }
    }, [nodeLibrary.browsing, hideNodeLibrary, showNodeLibrary]);

    const handleElementHover = useCallback((element: LibraryElement) => {
        setSelectedElement(element);
    }, []);

    const handleNodeLibraryKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                hideNodeLibrary();
                const containerElement = containerRef.current;
                if (containerElement) {
                    containerElement.focus();
                }
            }
        },
        [hideNodeLibrary, containerRef],
    );

    const renderNodeLibrary = useCallback(() => {
        if (!nodeLibrary.browsing) return null;

        return (
            <div
                className="absolute top-3 right-3 bottom-3 w-[300px] xl:w-[360px] z-10"
                style={{ pointerEvents: 'none' }}
                onContextMenu={event => {
                    event.preventDefault();
                    return false;
                }}
                onKeyDown={handleNodeLibraryKeyDown}
                tabIndex={0}
            >
                <div style={{ pointerEvents: 'auto', height: '100%' }}>
                    <NodeLibraryPanel
                        library={libraryElements}
                        onElementHover={handleElementHover}
                        onElementSelect={handleNodeLibraryNodeSelect}
                        selectedElement={selectedElement}
                    />
                </div>
            </div>
        );
    }, [nodeLibrary.browsing, handleNodeLibraryKeyDown, libraryElements, handleElementHover, handleNodeLibraryNodeSelect, selectedElement]);

    const renderPreviewNode = useCallback(() => {
        if (!nodeLibrary.browsing || !previewWorldPosition) return null;

        const nodeDefinition = selectedElement ? nodeDefinitions[selectedElement.nodeId] : undefined;

        return (
            <NodeLibraryPreview
                position={previewWorldPosition}
                element={selectedElement}
                nodeDefinition={nodeDefinition}
                functions={functions}
                callIntegration={callIntegration}
                onSelect={selectedElement ? () => handleNodeLibraryNodeSelect(selectedElement) : undefined}
            />
        );
    }, [nodeLibrary.browsing, selectedElement, previewWorldPosition, nodeDefinitions, functions, callIntegration, handleNodeLibraryNodeSelect]);

    return {
        nodeLibrary,
        showNodeLibrary,
        toggleNodeLibrary,
        toggleNodeLibraryAtCenter,
        hideNodeLibrary,
        renderNodeLibrary,
        renderPreviewNode,
    };
}
